import { useCallback, useMemo, useRef, useState } from "react";
import {
    applyAccountSelection,
    applyLineItemFieldChange,
    mapBackendLineItemValidationErrorsToFields,
    mergeLineEditFieldErrors,
    SEGMENT_TYPE_TO_FORM_KEY,
    validateLineEditForm,
} from "./lineItemEditValidation";
import { resolveLineItemFields } from "./lineItemFields";
import type { TReviewLineItem } from "./types";
import type { TApiAllowedSegmentValues } from "@/apis/proda_finance/types";
import type { TSearchableOption } from "@/ui/molecules/SearchableSelect";

type TUseLineItemEditorDeps = {
    fileDealIdForEdit: string | undefined;
    /** File type of the file under review — decides which fields are mandatory. */
    fileType: string | undefined;
    getSegmentAllowedValuesBatch: (
        accountCode: string,
        propertyCode?: string | null,
    ) => Promise<TApiAllowedSegmentValues>;
    /** Server-side master property-code search for the propcode dropdown. */
    searchMasterPropertyCodes: (query: string) => Promise<TSearchableOption[]>;
    /** Account-code field search: options display the code as label. */
    searchYardiAccountsByCode: (query: string) => Promise<TSearchableOption[]>;
    /** Account-name field search: options display the name as label. */
    searchYardiAccountsByName: (query: string) => Promise<TSearchableOption[]>;
    loadDealSegmentMappings: (dealId: string) => Promise<
        Array<{
            id: string;
            segment_type: number;
            source_segment_value: string;
            source_segment_name: string | null;
            target_segment_value: string;
            target_segment_name: string | null;
            is_active: boolean;
        }>
    >;
    onEditLineItem: (lineItem: TReviewLineItem, updates: Partial<TReviewLineItem>) => Promise<void>;
};

/**
 * Encapsulates the line-item edit modal: form state, dropdown data loading,
 * segment-allowed refetching, field validation, and save. Extracted from
 * ViewFileReview to keep the view focused on layout.
 */
export function useLineItemEditor({
    fileDealIdForEdit,
    fileType,
    getSegmentAllowedValuesBatch,
    searchMasterPropertyCodes,
    searchYardiAccountsByCode,
    searchYardiAccountsByName,
    loadDealSegmentMappings,
    onEditLineItem,
}: TUseLineItemEditorDeps) {
    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingLineItem, setEditingLineItem] = useState<TReviewLineItem | null>(null);
    const [editFormData, setEditFormData] = useState<Partial<TReviewLineItem>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);

    // Dropdown data for edit modal (propcode is now a SearchableSelect — no pre-loaded list)
    const [editSegmentMappings, setEditSegmentMappings] = useState<
        Array<{
            segment_type: number;
            source_segment_value: string;
            source_segment_name: string | null;
            target_segment_value: string;
        }>
    >([]);
    const [editSegmentAllowedValues, setEditSegmentAllowedValues] = useState<
        Record<number, string[]>
    >({});
    /**
     * Segment 4 allowed value -> property master name, for option labels only. Kept
     * apart from `editSegmentAllowedValues` so validation keeps comparing bare codes.
     */
    const [editSegment4Descriptions, setEditSegment4Descriptions] = useState<
        Record<string, string>
    >({});
    const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false);
    const segmentAllowedRefetchSeq = useRef(0);
    /** Inline field errors only after user edits or tries to save (avoids noisy red state on open). */
    const [lineEditShowFieldErrors, setLineEditShowFieldErrors] = useState(false);

    const refetchSegmentAllowedForEditForm = useCallback(
        async (form: Partial<TReviewLineItem>) => {
            const seq = ++segmentAllowedRefetchSeq.current;
            const accountCode = form.breAccountNumber ?? form.sourceAccountNumber ?? "";
            const propertyCode = form.propcode ?? form.localPropcode ?? null;

            if (!accountCode) {
                if (seq !== segmentAllowedRefetchSeq.current) return;
                setEditSegmentAllowedValues({});
                setEditSegment4Descriptions({});
                setEditFormData((prev) => ({
                    ...prev,
                    mappedSegment2: null,
                    mappedSegment4: null,
                    mappedSegment5: null,
                    mappedSegment6: null,
                    mappedSegment7: null,
                }));
                return;
            }

            try {
                const batch = await getSegmentAllowedValuesBatch(accountCode, propertyCode);
                if (seq !== segmentAllowedRefetchSeq.current) return;

                const record: Record<number, string[]> = {
                    2: batch.segment_2,
                    4: batch.segment_4,
                    5: batch.segment_5,
                    6: batch.segment_6,
                    7: batch.segment_7,
                };
                setEditSegmentAllowedValues(record);
                setEditSegment4Descriptions(batch.segment_4_descriptions ?? {});
                setEditFormData((prev) => {
                    const next = { ...prev };
                    for (const segType of [2, 4, 5, 6, 7] as const) {
                        const k = SEGMENT_TYPE_TO_FORM_KEY[segType];
                        const allowed = record[segType];
                        const val = next[k];
                        if (
                            allowed &&
                            allowed.length > 0 &&
                            val &&
                            !allowed.includes(String(val))
                        ) {
                            const cleared: Partial<TReviewLineItem> = { [k]: null };
                            Object.assign(next, cleared);
                        }
                    }
                    return next;
                });
            } catch {
                if (seq !== segmentAllowedRefetchSeq.current) return;
            }
        },
        [getSegmentAllowedValuesBatch],
    );

    /** Field definitions for this file type — FinJournals drop the required flags. */
    const editFields = useMemo(() => resolveLineItemFields(fileType), [fileType]);

    const mergedEditFieldErrors = useMemo(
        () =>
            mergeLineEditFieldErrors({
                server: mapBackendLineItemValidationErrorsToFields(
                    editingLineItem?.validationErrors ?? [],
                ),
                client: validateLineEditForm({
                    form: editFormData,
                    fields: editFields,
                    segmentAllowedValues: editSegmentAllowedValues,
                }),
                showClientErrors: lineEditShowFieldErrors,
                editingLineItem,
                editFormData,
            }),
        [
            editingLineItem,
            editFormData,
            lineEditShowFieldErrors,
            editSegmentAllowedValues,
            editFields,
        ],
    );

    // Handle edit click - open modal with line item data and fetch dropdown data
    const handleEditClick = useCallback(
        (lineItem: TReviewLineItem) => {
            segmentAllowedRefetchSeq.current += 1;
            setLineEditShowFieldErrors(false);
            setEditSegmentMappings([]);
            setEditSegmentAllowedValues({});
            setEditSegment4Descriptions({});
            setEditingLineItem(lineItem);
            setEditFormData({ ...lineItem });
            setEditError(null);
            setEditModalOpen(true);

            // Note: standalone FinJournal files have no deal, but the edit dropdowns
            // (propcode, CoA / Acc No + Acc Name, and segments 2/4/5/6/7) are still
            // resolved from company-wide master data, so we load them regardless of
            // whether the file belongs to a deal.
            setIsLoadingDropdowns(true);
            const accountCode = lineItem.breAccountNumber ?? lineItem.sourceAccountNumber ?? "";
            const propertyCode = lineItem.propcode ?? lineItem.localPropcode ?? null;

            Promise.all([
                fileDealIdForEdit
                    ? loadDealSegmentMappings(fileDealIdForEdit).catch(() => [])
                    : Promise.resolve([]),
                accountCode
                    ? getSegmentAllowedValuesBatch(accountCode, propertyCode).then((batch) => ({
                          allowed: {
                              2: batch.segment_2,
                              4: batch.segment_4,
                              5: batch.segment_5,
                              6: batch.segment_6,
                              7: batch.segment_7,
                          } as Record<number, string[]>,
                          segment4Descriptions: batch.segment_4_descriptions ?? {},
                      }))
                    : Promise.resolve({
                          allowed: {} as Record<number, string[]>,
                          segment4Descriptions: {} as Record<string, string>,
                      }),
            ])
                .then(([segMaps, allowedBySegment]) => {
                    setEditSegmentMappings(
                        segMaps
                            .filter((s) => s.is_active)
                            .map((s) => ({
                                segment_type: s.segment_type,
                                source_segment_value: s.source_segment_value,
                                source_segment_name: s.source_segment_name,
                                target_segment_value: s.target_segment_value,
                            })),
                    );
                    setEditSegmentAllowedValues(allowedBySegment.allowed);
                    setEditSegment4Descriptions(allowedBySegment.segment4Descriptions);
                })
                .finally(() => setIsLoadingDropdowns(false));
        },
        [fileDealIdForEdit, loadDealSegmentMappings, getSegmentAllowedValuesBatch],
    );
    // Handle form field change for non-account fields with segment-allowed refetch
    const handleFieldChange = useCallback(
        (key: keyof TReviewLineItem, value: string | number) => {
            setLineEditShowFieldErrors(true);
            setEditFormData((prev) => {
                const next = applyLineItemFieldChange(prev, key, value);
                const shouldRefetch = key === "propcode" || key === "localPropcode";
                if (shouldRefetch) {
                    void Promise.resolve().then(() => refetchSegmentAllowedForEditForm(next));
                }
                return next;
            });
            setEditError(null);
        },
        [refetchSegmentAllowedForEditForm],
    );

    // Handle account selection from either the code or name SearchableSelect
    const handleAccountSelect = useCallback(
        (option: TSearchableOption | null) => {
            setLineEditShowFieldErrors(true);
            setEditFormData((prev) => {
                const next = applyAccountSelection(prev, option);
                void Promise.resolve().then(() => refetchSegmentAllowedForEditForm(next));
                return next;
            });
            setEditError(null);
        },
        [refetchSegmentAllowedForEditForm],
    );

    // Handle propcode selection from the master-data SearchableSelect.
    // Sets propcode to the selected master property code and triggers segment refetch.
    const handlePropcodeSelect = useCallback(
        (option: TSearchableOption | null) => {
            setLineEditShowFieldErrors(true);
            setEditFormData((prev) => {
                const next: Partial<TReviewLineItem> = {
                    ...prev,
                    propcode: option ? option.value : null,
                };
                void Promise.resolve().then(() => refetchSegmentAllowedForEditForm(next));
                return next;
            });
            setEditError(null);
        },
        [refetchSegmentAllowedForEditForm],
    );

    // Handle save with required field validation
    const handleSaveLineItem = useCallback(async () => {
        if (!editingLineItem) return;

        const snapshotErrors = mergeLineEditFieldErrors({
            server: mapBackendLineItemValidationErrorsToFields(
                editingLineItem.validationErrors ?? [],
            ),
            client: validateLineEditForm({
                form: editFormData,
                fields: editFields,
                segmentAllowedValues: editSegmentAllowedValues,
            }),
            showClientErrors: true,
            editingLineItem,
            editFormData,
        });
        if (Object.keys(snapshotErrors).length > 0) {
            setLineEditShowFieldErrors(true);
            const firstInvalid = editFields.find((f) => snapshotErrors[f.key]);
            if (firstInvalid) {
                window.requestAnimationFrame(() => {
                    document.getElementById(`edit-${String(firstInvalid.key)}`)?.focus();
                });
            }
            return;
        }

        // Capture before clearing so the async call still has the data
        const lineItemToSave = editingLineItem;
        const formDataToSave = editFormData;

        // Close immediately so the background reload doesn't show the modal again
        setEditModalOpen(false);
        setEditingLineItem(null);
        setEditFormData({});
        setIsSaving(true);
        setEditError(null);
        try {
            await onEditLineItem(lineItemToSave, formDataToSave);
            // onEditLineItem already revalidates and reloads file + line items
        } catch (error) {
            console.error("Failed to save line item:", error);
            // Re-open with the original data so the user can correct the error
            setEditingLineItem(lineItemToSave);
            setEditFormData(formDataToSave);
            setEditError(error instanceof Error ? error.message : "Failed to save line item");
            setEditModalOpen(true);
        } finally {
            setIsSaving(false);
        }
    }, [editingLineItem, editFormData, editSegmentAllowedValues, editFields, onEditLineItem]);

    // Handle modal close
    const handleCloseEditModal = useCallback((open: boolean) => {
        if (!open) {
            setEditModalOpen(false);
            setEditingLineItem(null);
            setEditFormData({});
            setEditError(null);
            setLineEditShowFieldErrors(false);
        }
    }, []);

    return {
        editModalOpen,
        editingLineItem,
        editFormData,
        isSaving,
        editError,
        editSegmentMappings,
        editSegmentAllowedValues,
        editSegment4Descriptions,
        isLoadingDropdowns,
        editFields,
        mergedEditFieldErrors,
        handleEditClick,
        handleFieldChange,
        handleAccountSelect,
        handlePropcodeSelect,
        searchMasterPropertyCodes,
        searchYardiAccountsByCode,
        searchYardiAccountsByName,
        handleSaveLineItem,
        handleCloseEditModal,
    };
}
